import React, { useMemo } from 'react';
import { Button, Input, DatePicker, Space, Popconfirm, Table, Tag, Select } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

export interface CreateBusinessMemberData {
  id?: number;
  type?: 'customer' | 'business';
  reference_id?: number;
  customer_id?: number;
  name?: string;
  identification_no?: string;
  appoint_date?: string;
  resign_date?: string;
  share?: number;
  _isDeletedLocal?: boolean;
  _isNewFromRequest?: boolean;
  _addRequest?: any;
  _deleteRequest?: any;
  _crIndex?: number;
  _actualIndex?: number;
}

interface MembersTabProps {
  members: CreateBusinessMemberData[];
  addMember: () => void;
  removeMember: (index: number) => void;
  updateMember: (index: number, field: keyof CreateBusinessMemberData, value: string | number | undefined) => void;
  isEdit: boolean;
  isEditing?: boolean;
  isCustomerPortal?: boolean;
  deletedMembers?: Array<{ index: number; data: any }>;
  changeRequests?: Array<{ field_name: string; status: string; old_value?: string; new_value?: string; id: number }>;
  getMemberDisplayValue?: (index: number, field: keyof CreateBusinessMemberData) => string;
  handleMemberFieldChange?: (index: number, field: keyof CreateBusinessMemberData, value: string | number | undefined) => void;
  handleApproveChangeRequest?: (requestId: number) => void;
  handleRejectChangeRequest?: (requestId: number) => void;
  handleApproveMemberDelete?: (requestId: number) => void;
  handleRejectMemberDelete?: (requestId: number) => void;
  customers?: Array<{ id: number; name: string; ic_passport_no?: string }>;
  businesses?: Array<{ id: number; name: string }>;
}

export const BusinessMembersTab: React.FC<MembersTabProps> = ({
  members,
  addMember,
  removeMember,
  updateMember,
  isEdit,
  isEditing = false,
  isCustomerPortal = false,
  deletedMembers = [],
  changeRequests = [],
  getMemberDisplayValue,
  handleMemberFieldChange,
  handleApproveChangeRequest,
  handleRejectChangeRequest,
  handleApproveMemberDelete,
  handleRejectMemberDelete,
  customers = [],
  businesses = [],
}) => {
  // In create mode (!isEdit), always allow editing. In edit mode, use isEditing state.
  const canEdit = !isEdit || isEditing;
  const totalShare = members.reduce((sum, member) => sum + (Number(member.share) || 0), 0);

  const getFieldChangeRequest = (index: number, field: keyof CreateBusinessMemberData) =>
    changeRequests.find((cr) => cr.status === 'pending' && cr.field_name === `member_${index}_${field}`);

  const getDeleteChangeRequest = (index: number) =>
    changeRequests.find((cr) => cr.status === 'pending' && cr.field_name === `member_${index}_delete`);

  const processedMembers = useMemo(() => {
    const deletedKeys = new Set<string>();
    deletedMembers.forEach((d) => {
      if (d.data) {
        const key = `${d.data.id || ''}_${d.data.type || ''}_${d.data.reference_id || d.data.customer_id || ''}_${d.data.identification_no || ''}`;
        deletedKeys.add(key);
      }
    });

    const all: any[] = [];
    members.forEach((mem, currentIdx) => {
      const key = `${mem.id || ''}_${mem.type || ''}_${mem.reference_id || mem.customer_id || ''}_${mem.identification_no || ''}`;
      if (!deletedKeys.has(key)) {
        let originalIdx = currentIdx;
        deletedMembers.forEach((d) => {
          if (d.index <= currentIdx) originalIdx++;
        });
        all.push({ ...mem, _crIndex: originalIdx, _actualIndex: currentIdx });
      }
    });

    all.sort((a, b) => a._crIndex - b._crIndex);

    const deletedSorted = [...deletedMembers].sort((a, b) => b.index - a.index);
    deletedSorted.forEach((d) => {
      if (d.data) {
        const key = `${d.data.id || ''}_${d.data.type || ''}_${d.data.reference_id || d.data.customer_id || ''}_${d.data.identification_no || ''}`;
        const exists = all.some((mem) => `${mem.id || ''}_${mem.type || ''}_${mem.reference_id || mem.customer_id || ''}_${mem.identification_no || ''}` === key);
        if (!exists) {
          all.splice(d.index, 0, { ...d.data, _crIndex: d.index, _isDeletedLocal: true });
        }
      }
    });

    // Track add request index for proper ordering
    let addRequestIndex = 0;
    changeRequests.forEach((cr) => {
      if (cr.status === 'pending' && cr.field_name === 'member_add') {
        // Handle member_add format: contains a single member object (not an array)
        try {
          const mem = JSON.parse(cr.new_value || '{}');
          const key = `${mem.id || ''}_${mem.type || ''}_${mem.reference_id || mem.customer_id || ''}_${mem.identification_no || ''}`;
          const exists = all.some((m) => `${m.id || ''}_${m.type || ''}_${m.reference_id || m.customer_id || ''}_${m.identification_no || ''}` === key);
          if (!exists) {
            all.push({ ...mem, _isNewFromRequest: true, _addRequest: cr, _crIndex: 999 + addRequestIndex });
            addRequestIndex++;
          }
        } catch (_) {}
      } else if (cr.status === 'pending' && cr.field_name.startsWith('member_') && cr.field_name.endsWith('_add')) {
        const parts = cr.field_name.split('_');
        if (parts.length >= 3) {
          const index = parseInt(parts[1], 10);
          try {
            const mem = JSON.parse(cr.new_value || '{}');
            const key = `${mem.id || ''}_${mem.type || ''}_${mem.reference_id || mem.customer_id || ''}_${mem.identification_no || ''}`;
            const exists = all.some((m) => `${m.id || ''}_${m.type || ''}_${m.reference_id || m.customer_id || ''}_${m.identification_no || ''}` === key);
            if (!exists) {
              all.push({ ...mem, _isNewFromRequest: true, _addRequest: cr, _crIndex: index });
            }
          } catch (_) {}
        }
      }
    });

    const data = all.map((mem, idx) => {
      const crIdx = mem._crIndex !== undefined ? mem._crIndex : idx;
      const actualIdx = mem._actualIndex !== undefined ? mem._actualIndex : idx;
      const isDeleted =
        mem._isDeletedLocal ||
        deletedMembers.some((d) => d.index === crIdx) ||
        getDeleteChangeRequest(crIdx) !== undefined;
      const deleteReq = getDeleteChangeRequest(crIdx);
      return {
        ...mem,
        key: `member-${crIdx}-${idx}-${mem.id || 'new'}-${mem._isNewFromRequest ? 'new-request' : ''}`,
        originalIndex: crIdx,
        actualIndex: actualIdx,
        isDeleted,
        deleteRequest: deleteReq,
        isNewFromRequest: mem._isNewFromRequest || false,
        addRequest: mem._addRequest,
      };
    });

    const getRowWeight = (record: any) => {
      if (record.isDeleted) return 0;
      if (record.isNewFromRequest) return 1;
      if (!record.id) return 2;
      return 0;
    };

    data.sort((a: any, b: any) => {
      const wA = getRowWeight(a);
      const wB = getRowWeight(b);
      if (wA !== wB) return wA - wB;
      return a.originalIndex - b.originalIndex;
    });

    return data;
  }, [members, deletedMembers, changeRequests]);

  const columns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: '14%',
      render: (value: string, record: any) => {
        const cellStyle = record.isDeleted ? { textDecoration: 'line-through', color: '#999' } : {};
        const cr = getFieldChangeRequest(record.originalIndex, 'type');

        if (canEdit && !record.isDeleted) {
          let displayValue = value || (record.customer_id ? 'customer' : undefined);
          if (cr?.new_value) displayValue = cr.new_value;
          else if (record.isNewFromRequest && record.addRequest) {
            try {
              const parsed = JSON.parse(record.addRequest.new_value || '{}');
              const d = Array.isArray(parsed) ? parsed[0] : parsed;
              displayValue = d?.type || displayValue;
            } catch {
              displayValue = value || undefined;
            }
          } else if (getMemberDisplayValue) {
            const idx = record.actualIndex ?? record.originalIndex;
            const pending = getMemberDisplayValue(idx, 'type');
            if (pending !== '') displayValue = pending as any;
          }

          return (
            <Select
              placeholder="Select Type"
              value={displayValue}
              onChange={(typeValue) => {
                const idx = record.actualIndex ?? record.originalIndex;
                if (handleMemberFieldChange) {
                  handleMemberFieldChange(idx, 'type', typeValue);
                  handleMemberFieldChange(idx, 'reference_id', undefined);
                  handleMemberFieldChange(idx, 'customer_id', undefined);
                } else {
                  updateMember(idx, 'type', typeValue);
                  updateMember(idx, 'reference_id', undefined);
                  updateMember(idx, 'customer_id', undefined);
                }
              }}
              style={{ width: '100%' }}
              options={[
                { label: 'Customer', value: 'customer' },
                { label: 'Business', value: 'business' },
              ]}
            />
          );
        }

        if (cr) {
          if (cr.status === 'approved') return <span>{cr.new_value || value || '-'}</span>;
          if (cr.status === 'rejected') return <span>{cr.old_value || value || '-'}</span>;
          return (
            <div>
              <span style={{ textDecoration: 'line-through', color: '#999', marginRight: 8 }}>{cr.old_value || value || '-'}</span>
              <span style={{ color: '#ff9800' }}>{cr.new_value || '-'}</span>
            </div>
          );
        }

        return <span style={cellStyle}>{value || '-'}</span>;
      },
    },
    {
      title: 'Reference ID',
      dataIndex: 'reference_id',
      key: 'reference_id',
      width: '22%',
      render: (referenceId: number, record: any) => {
        const cellStyle = record.isDeleted ? { textDecoration: 'line-through', color: '#999' } : {};
        const cr = getFieldChangeRequest(record.originalIndex, 'reference_id');
        const memberType = record.type || (record.customer_id ? 'customer' : undefined);
        const options = memberType === 'business' ? businesses : customers;
        const resolvedReferenceId = referenceId || record.customer_id;

        if (canEdit && !record.isDeleted) {
          let displayValue = resolvedReferenceId || undefined;
          if (cr?.new_value) displayValue = parseInt(cr.new_value, 10);
          else if (record.isNewFromRequest && record.addRequest) {
            try {
              const parsed = JSON.parse(record.addRequest.new_value || '{}');
              const d = Array.isArray(parsed) ? parsed[0] : parsed;
              displayValue = d?.reference_id || d?.customer_id || displayValue;
            } catch {
              displayValue = resolvedReferenceId || undefined;
            }
          } else if (getMemberDisplayValue) {
            const idx = record.actualIndex ?? record.originalIndex;
            const pending = getMemberDisplayValue(idx, 'reference_id');
            if (pending !== '') displayValue = parseInt(pending, 10);
          }

          return (
            <Select
              placeholder={memberType === 'business' ? 'Select Business' : 'Select Customer'}
              allowClear
              showSearch
              optionFilterProp="children"
              value={displayValue}
              onChange={(value) => {
                const idx = record.actualIndex ?? record.originalIndex;
                if (handleMemberFieldChange) {
                  handleMemberFieldChange(idx, 'reference_id', value);
                  handleMemberFieldChange(idx, 'customer_id', memberType === 'customer' ? value : undefined);
                  if (memberType === 'customer' && value) {
                    const selectedCustomer = customers.find(c => c.id === value);
                    if (selectedCustomer?.ic_passport_no) {
                      handleMemberFieldChange(idx, 'identification_no', selectedCustomer.ic_passport_no);
                    }
                  }
                } else if (updateMember) {
                  updateMember(idx, 'reference_id', value);
                  updateMember(idx, 'customer_id', memberType === 'customer' ? value : undefined);
                  if (memberType === 'customer' && value) {
                    const selectedCustomer = customers.find(c => c.id === value);
                    if (selectedCustomer?.ic_passport_no) {
                      updateMember(idx, 'identification_no', selectedCustomer.ic_passport_no);
                    }
                  }
                }
              }}
              style={{ width: '100%' }}
            >
              {options.map((option) => (
                <Select.Option key={option.id} value={option.id}>
                  {option.name} (#{option.id})
                </Select.Option>
              ))}
            </Select>
          );
        }

        if (cr) {
          const oldOption = options.find(option => option.id === parseInt(cr.old_value || '0', 10));
          const newOption = options.find(option => option.id === parseInt(cr.new_value || '0', 10));
          if (cr.status === 'approved') return <span>{newOption?.name || '-'}</span>;
          if (cr.status === 'rejected') return <span>{oldOption?.name || '-'}</span>;
          return (
            <div>
              <span style={{ textDecoration: 'line-through', color: '#999', marginRight: 8 }}>{oldOption?.name || '-'}</span>
              <span style={{ color: '#ff9800' }}>{newOption?.name || '-'}</span>
            </div>
          );
        }

        const resolvedOption = options.find(option => option.id === resolvedReferenceId);
        return <span style={cellStyle}>{resolvedOption?.name || '-'}</span>;
      },
    },
    {
      title: 'Identification No.',
      dataIndex: 'identification_no',
      key: 'identification_no',
      width: '22%',
      render: (value: string, record: any) => {
        const cellStyle = record.isDeleted ? { textDecoration: 'line-through', color: '#999' } : {};
        const cr = getFieldChangeRequest(record.originalIndex, 'identification_no');
        if (canEdit && !record.isDeleted) {
          let displayValue = value || '';
          if (cr?.new_value) displayValue = cr.new_value;
          else if (record.isNewFromRequest && record.addRequest) {
            try {
              const parsed = JSON.parse(record.addRequest.new_value || '{}');
              const d = Array.isArray(parsed) ? parsed[0] : parsed;
              displayValue = d?.identification_no || displayValue;
            } catch {
              displayValue = value || '';
            }
          } else if (getMemberDisplayValue) {
            const idx = record.actualIndex ?? record.originalIndex;
            const pending = getMemberDisplayValue(idx, 'identification_no');
            if (pending !== '') displayValue = pending;
          }
          return (
            <Input
              value={displayValue}
              onChange={(e) => {
                const val = e.target.value;
                const idx = record.actualIndex ?? record.originalIndex;
                if (handleMemberFieldChange) handleMemberFieldChange(idx, 'identification_no', val);
                else updateMember(idx, 'identification_no', val);
              }}
              placeholder="Identification No."
            />
          );
        }
        if (cr) {
          if (cr.status === 'approved') return <span>{cr.new_value || value || '-'}</span>;
          if (cr.status === 'rejected') return <span>{cr.old_value || value || '-'}</span>;
          return (
            <div>
              <span style={{ textDecoration: 'line-through', color: '#999', marginRight: 8 }}>{cr.old_value || value || '-'}</span>
              <span style={{ color: '#ff9800' }}>{cr.new_value || '-'}</span>
            </div>
          );
        }
        return <span style={cellStyle}>{value || '-'}</span>;
      },
    },
    {
      title: 'Share',
      dataIndex: 'share',
      key: 'share',
      width: '12%',
      render: (value: number, record: any) => {
        const cellStyle = record.isDeleted ? { textDecoration: 'line-through', color: '#999' } : {};
        const cr = getFieldChangeRequest(record.originalIndex, 'share');
        if (canEdit && !record.isDeleted) {
          let displayValue = value ?? 0;
          if (cr?.new_value) displayValue = Number(cr.new_value);
          else if (getMemberDisplayValue) {
            const idx = record.actualIndex ?? record.originalIndex;
            const pending = getMemberDisplayValue(idx, 'share');
            if (pending !== '') displayValue = Number(pending);
          }
          return (
            <Input
              type="number"
              min={0}
              step={0.01}
              value={displayValue}
              onChange={(e) => {
                const val = e.target.value === '' ? 0 : parseFloat(e.target.value);
                const idx = record.actualIndex ?? record.originalIndex;
                if (handleMemberFieldChange) handleMemberFieldChange(idx, 'share', isNaN(val) ? 0 : val);
                else updateMember(idx, 'share', isNaN(val) ? 0 : val);
              }}
              placeholder="Share"
            />
          );
        }
        if (cr) {
          if (cr.status === 'approved') return <span>{Number(cr.new_value || value || 0).toFixed(2)}</span>;
          if (cr.status === 'rejected') return <span>{Number(cr.old_value || value || 0).toFixed(2)}</span>;
          return (
            <div>
              <span style={{ textDecoration: 'line-through', color: '#999', marginRight: 8 }}>
                {Number(cr.old_value || value || 0).toFixed(2)}
              </span>
              <span style={{ color: '#ff9800' }}>{Number(cr.new_value || 0).toFixed(2)}</span>
            </div>
          );
        }
        return <span style={cellStyle}>{value !== undefined ? Number(value).toFixed(2) : '-'}</span>;
      },
    },
    {
      title: 'Appoint Date',
      dataIndex: 'appoint_date',
      key: 'appoint_date',
      width: '20%',
      render: (value: string, record: any) => {
        const cellStyle = record.isDeleted ? { textDecoration: 'line-through', color: '#999' } : {};
        const cr = getFieldChangeRequest(record.originalIndex, 'appoint_date');
        const formatDate = (val?: string) => (val ? dayjs(val).format('DD/MM/YYYY') : '-');
        if (canEdit && !record.isDeleted) {
          let displayValue = value ? dayjs(value) : null;
          if (cr?.new_value) displayValue = cr.new_value ? dayjs(cr.new_value) : null;
          return (
                <DatePicker
                  style={{ width: '100%' }}
              value={displayValue}
              onChange={(date) => {
                const val = date ? date.format('YYYY-MM-DD') : '';
                const idx = record.actualIndex ?? record.originalIndex;
                if (handleMemberFieldChange) handleMemberFieldChange(idx, 'appoint_date', val);
                else updateMember(idx, 'appoint_date', val);
              }}
              placeholder="Appoint date"
            />
          );
        }
        if (cr) {
          if (cr.status === 'approved') return <span>{formatDate(cr.new_value || value)}</span>;
          if (cr.status === 'rejected') return <span>{formatDate(cr.old_value || value)}</span>;
          return (
            <div>
              <span style={{ textDecoration: 'line-through', color: '#999', marginRight: 8 }}>{formatDate(cr.old_value || value)}</span>
              <span style={{ color: '#ff9800' }}>{formatDate(cr.new_value)}</span>
            </div>
          );
        }
        return <span style={cellStyle}>{formatDate(value)}</span>;
      },
    },
    {
      title: 'Resign Date',
      dataIndex: 'resign_date',
      key: 'resign_date',
      width: '12%',
      render: (value: string, record: any) => {
        const cellStyle = record.isDeleted ? { textDecoration: 'line-through', color: '#999' } : {};
        const cr = getFieldChangeRequest(record.originalIndex, 'resign_date');
        const formatDate = (val?: string) => (val ? dayjs(val).format('DD/MM/YYYY') : '-');
        if (canEdit && !record.isDeleted) {
          let displayValue = value ? dayjs(value) : null;
          if (cr?.new_value) displayValue = cr.new_value ? dayjs(cr.new_value) : null;
          return (
                <DatePicker
                  style={{ width: '100%' }}
              value={displayValue}
              onChange={(date) => {
                const val = date ? date.format('YYYY-MM-DD') : '';
                const idx = record.actualIndex ?? record.originalIndex;
                if (handleMemberFieldChange) handleMemberFieldChange(idx, 'resign_date', val);
                else updateMember(idx, 'resign_date', val);
              }}
              placeholder="Resign date"
            />
          );
        }
        if (cr) {
          if (cr.status === 'approved') return <span>{formatDate(cr.new_value || value)}</span>;
          if (cr.status === 'rejected') return <span>{formatDate(cr.old_value || value)}</span>;
          return (
            <div>
              <span style={{ textDecoration: 'line-through', color: '#999', marginRight: 8 }}>{formatDate(cr.old_value || value)}</span>
              <span style={{ color: '#ff9800' }}>{formatDate(cr.new_value)}</span>
            </div>
          );
        }
        return <span style={cellStyle}>{formatDate(value)}</span>;
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: '8%',
      render: (_: any, record: any) => {
        if (record.isDeleted || record.deleteRequest) {
          if (!isEditing) return null;
          if (isCustomerPortal) return <Tag color="orange">Pending</Tag>;
          const deleteReq = record.deleteRequest || changeRequests.find(
            (cr) => cr.field_name === `member_${record.originalIndex}_delete` && cr.status === 'pending'
          );
          if (deleteReq) {
            return (
              <Space>
                <Button
                  type="primary"
                  size="small"
                  style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                  onClick={() => handleApproveMemberDelete && handleApproveMemberDelete(deleteReq.id)}
                >
                  Approve
                </Button>
                <Button
                  danger
                  size="small"
                  onClick={() => handleRejectMemberDelete && handleRejectMemberDelete(deleteReq.id)}
                >
                  Reject
                </Button>
              </Space>
            );
          }
        }

        if (record.isNewFromRequest && record.addRequest) {
          if (!isEditing) return null;
          if (isCustomerPortal) return <Tag color="orange">Pending</Tag>;
          return (
            <Space>
              <Button
                type="primary"
                size="small"
                style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                onClick={() => handleApproveChangeRequest && handleApproveChangeRequest(record.addRequest.id)}
              >
                Approve
              </Button>
              <Button
                danger
                size="small"
                onClick={() => handleRejectChangeRequest && handleRejectChangeRequest(record.addRequest.id)}
              >
                Reject
              </Button>
            </Space>
          );
        }

        const fieldCRs = [
          getFieldChangeRequest(record.originalIndex, 'type'),
          getFieldChangeRequest(record.originalIndex, 'reference_id'),
          getFieldChangeRequest(record.originalIndex, 'identification_no'),
          getFieldChangeRequest(record.originalIndex, 'share'),
          getFieldChangeRequest(record.originalIndex, 'appoint_date'),
          getFieldChangeRequest(record.originalIndex, 'resign_date'),
        ].filter(Boolean);

        if (fieldCRs.length > 0) {
          if (!isEditing) return null;
          if (isCustomerPortal) return <Tag color="orange">Pending</Tag>;
          return (
            <Space>
              {fieldCRs.map((cr, idx) => (
                <React.Fragment key={idx}>
                  <Button
                    type="primary"
                    size="small"
                    style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                    onClick={() => handleApproveChangeRequest && handleApproveChangeRequest(cr!.id)}
                  >
                    Approve
                  </Button>
        <Button
          danger
                    size="small"
                    onClick={() => handleRejectChangeRequest && handleRejectChangeRequest(cr!.id)}
                  >
                    Reject
                  </Button>
                </React.Fragment>
              ))}
            </Space>
          );
        }

        if (canEdit && !record.isDeleted && !record.isNewFromRequest) {
          const idx = record.actualIndex ?? record.originalIndex;
          return (
            <Popconfirm
              title="Are you sure you want to delete this member?"
              onConfirm={() => removeMember(idx)}
              okText="Yes"
              cancelText="No"
            >
              <Button danger size="small" icon={<DeleteOutlined />}>
          Delete
        </Button>
            </Popconfirm>
          );
        }
        return null;
      },
    },
  ];

  return (
    <div>
      <Table
        columns={columns}
        dataSource={processedMembers}
        pagination={false}
        rowKey="key"
        onRow={(record: any) => {
          const isDeleted = record.isDeleted;
          const isNew = record.isNewFromRequest;
          const hasPendingFieldChange = changeRequests.some(
            (cr) =>
              cr.status === 'pending' &&
              cr.field_name.startsWith(`member_${record.originalIndex}_`) &&
              !cr.field_name.endsWith('_add') &&
              !cr.field_name.endsWith('_delete') &&
              cr.field_name !== 'member_add'
          );
          return {
            style: {
              backgroundColor: isDeleted ? '#ffebee' : isNew ? '#e3f2fd' : hasPendingFieldChange ? '#fff3cd' : undefined,
            },
          };
        }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
        <div style={{ fontWeight: 600 }}>Total</div>
        <div style={{ fontWeight: 600 }}>{totalShare.toFixed(2)}</div>
      </div>
      {canEdit && processedMembers.filter((m: any) => !m.isDeleted && !m.isNewFromRequest).length < 10 && (
        <div style={{ marginTop: 12 }}>
          <Button type="link" style={{ color: '#ff4d4f', padding: 0 }} onClick={addMember} icon={<PlusOutlined />}>
            (max 10)
      </Button>
        </div>
      )}
    </div>
  );
};
